import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import type { ControlClient } from "../api/client.ts";
import type { AgentVersions, AuditLogRow, Recording } from "../api/parse.ts";
import { loadRecording, type RecordingPlayback } from "../recording.ts";
import { authTimeStale, bearerAuthTime, operatorWho, type OperatorAuth } from "../auth.ts";
import type { PortalConfig } from "../config.ts";
import { correctedNow, serverClockOffsetMs } from "../desk/consentClock.ts";
import type { Desk } from "../desk/controller.ts";
import { holdLocalClipboard, placeLocalClipboard } from "../desk/localClipboard.ts";
import { isPng } from "../desk/messages.ts";
import { isConnectFixture, openSimulatedConnect } from "../desk/connectFixture.ts";
import { isMissFixture, openSimulatedMiss } from "../desk/missFixture.ts";
import { bootSessionDocument, canonicalSettingsPath, createSessionWindows, readPortalDocument, type SessionLaunch } from "../session-document.ts";
import { releaseQualityOnSignOut } from "./quality.tsx";
import { consentClickLine } from "../consentTrail.ts";
import {
  FIXTURE_ROLES,
  FIXTURE_SCOPE_CATALOG,
  FIXTURE_STATUS_USERS,
  FIXTURE_USERS,
  createDirectoryRole,
  createDirectoryUser,
  deleteDirectoryRole,
  deleteDirectoryUser,
  loadDirectoryRoles,
  loadDirectoryUsers,
  loadMenuAccess,
  loadScopeCatalog,
  readsAudit,
  readsDirectory,
  readsRecording,
  canDeleteDevices,
  portalDeviceDeleteEnabled,
  scopeWord,
  setDirectoryDisabled,
  setDirectoryUserRoles,
  updateDirectoryRole,
  updateDirectoryUser,
  type DirectoryRole,
  type DirectoryUser,
  type MenuAccess,
  type RoleGrant,
  type ScopeCatalog,
} from "./access.ts";
import { AuditLog, RolesScreen, SettingsOverview, SettingsShell, UsersScreen } from "./admin.tsx";
import { AUDIT_REFETCH_DEBOUNCE_MS, collectAuditEvents, readAuditFilters, serverAuditKey, writeAuditFilters, type AuditFilters } from "./auditQuery.ts";
import { RecordingPage } from "./recording-settings.tsx";
import { PortalChrome, applyDocumentMark } from "./chrome.tsx";
import { DeviceList, DevicePlacement } from "./devices.tsx";
import { useDeviceInventory } from "./inventory.ts";
import { InputProbe } from "./inputProbe.tsx";
import { recordingDownloadName } from "../api/recordingName.ts";
import { applyViewerReadings, watchViewerLink, type ViewerReading } from "../desk/viewerStats.ts";
import { emptyArrivalLog, noteQualityArrival, QUALITY_POLL_MS, type ArrivalLog } from "./qualityArrival.ts";
import { QualityProbe, type SessionQualityView } from "./quality.tsx";
import { SessionRecordings } from "./sessionRecordings.tsx";
import { SessionScreen } from "./session.tsx";
import { StealthDocument } from "./stealth.tsx";
import { demandStepUp, isStepUpError, visibleFailure } from "../stepUp.ts";
import { openStealthSession } from "./stealth-api.ts";

function fixtureMedia(search: string) {
  if (isConnectFixture(search)) return openSimulatedConnect;
  if (isMissFixture(search)) return openSimulatedMiss;
  return undefined;
}

const POPUP_BLOCKED = "The browser blocked the session window. Allow pop-ups for this site and try again.";
const PENDING_STEALTH = "wylo-rc-stealth-pending";

function rememberStealth(deviceId: string, justification: string, reason: string) {
  sessionStorage.setItem(PENDING_STEALTH, JSON.stringify({ deviceId, justification, reason }));
}

function takeStealth(): { deviceId: string; justification: string; reason: string } | null {
  const raw = sessionStorage.getItem(PENDING_STEALTH);
  sessionStorage.removeItem(PENDING_STEALTH);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { deviceId?: unknown; justification?: unknown; reason?: unknown };
    if (typeof parsed.deviceId !== "string" || typeof parsed.justification !== "string") return null;
    const justification = parsed.justification.trim();
    if (parsed.deviceId.length === 0 || justification.length === 0) return null;
    return { deviceId: parsed.deviceId, justification, reason: typeof parsed.reason === "string" ? parsed.reason : "" };
  } catch {
    return null;
  }
}
const ALREADY_STARTED = "This session window already started. Return to Devices to start again.";

export function PortalApp(props: {
  config: PortalConfig;
  auth: OperatorAuth;
  client: ControlClient;
  access?: MenuAccess | null;
}) {
  useEffect(() => {
    applyDocumentMark(props.config.environment);
  }, [props.config.environment]);
  if (import.meta.env.DEV && new URLSearchParams(window.location.search).get("wyloVerify") === "input") {
    return <InputProbe />;
  }
  if (import.meta.env.DEV && new URLSearchParams(window.location.search).get("wyloVerify") === "quality") {
    return <QualityProbe />;
  }
  const document = readPortalDocument(window.location);
  if (document.role === "devices") {
    return <OperatorDevices config={props.config} auth={props.auth} client={props.client} access={props.access} />;
  }
  if (document.role === "admin") {
    return (
      <AdminDocument
        config={props.config}
        auth={props.auth}
        client={props.client}
        access={props.access}
        page={document.page}
      />
    );
  }
  if (document.role === "recordings") {
    return <RecordingsDocument config={props.config} auth={props.auth} client={props.client} access={props.access} />;
  }
  if (document.role === "stealth") {
    return <StealthDocument config={props.config} auth={props.auth} sessionId={document.sessionId} />;
  }
  return <SessionDocument config={props.config} auth={props.auth} client={props.client} />;
}

function useResolvedAccess(apiBase: string, auth: OperatorAuth, given: MenuAccess | null | undefined): MenuAccess | null {
  const [loaded, setLoaded] = useState<MenuAccess | null>(given ?? null);
  useEffect(() => {
    if (given !== undefined) {
      setLoaded(given);
      return;
    }
    if (auth.account === null) return;
    const controller = new AbortController();
    void loadMenuAccess(apiBase, () => auth.token(), controller.signal)
      .then((next) => {
        if (!controller.signal.aborted && next) setLoaded(next);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        const message = visibleFailure(cause, "");
        if (message) setLoaded(null);
      });
    return () => controller.abort();
  }, [apiBase, auth, given]);
  return given === undefined ? loaded : given;
}

function adminHref(): string {
  const verify = new URLSearchParams(window.location.search).get("wyloVerify");
  if (import.meta.env.DEV && verify) return `/settings?wyloVerify=${encodeURIComponent(verify)}`;
  return "/settings";
}

function RecordingsDocument(props: {
  config: PortalConfig;
  auth: OperatorAuth;
  client: ControlClient;
  access?: MenuAccess | null;
}) {
  const access = useResolvedAccess(props.config.apiBase, props.auth, props.access);
  const signedIn = props.auth.account !== null;
  const [items, setItems] = useState<Recording[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [playbackKind, setPlaybackKind] = useState<"video" | "transcript">("video");
  const [playbackText, setPlaybackText] = useState("");
  const videoRef = useRef<HTMLVideoElement>(null);
  const playback = useRef<RecordingPlayback | null>(null);

  useEffect(() => {
    if (!signedIn) return;
    let live = true;
    void props.client
      .listCatalogue()
      .then((page) => {
        if (live) setItems(page.items);
      })
      .catch((cause: unknown) => {
        const message = visibleFailure(cause, "Could not load recordings");
        if (live && message) setError(message);
      });
    return () => {
      live = false;
      playback.current?.release();
    };
  }, [props.client, signedIn]);

  async function play(recordingId: string) {
    setError(null);
    try {
      const source = await props.client.recordingContent(recordingId, "Play");
      if (source.type === "text/x-powershell-transcript" || source.type.startsWith("text/x-powershell-transcript;")) {
        playback.current?.release();
        playback.current = null;
        setPlaybackKind("transcript");
        setPlaybackText(await readBlob(source));
        setPlaybackUrl("transcript");
        return;
      }
      const loaded = await loadRecording({
        recordingId,
        intent: "Play",
        bytes: async () => source,
      });
      playback.current?.release();
      playback.current = loaded;
      setPlaybackKind("video");
      setPlaybackUrl(loaded.objectUrl);
      queueMicrotask(() => {
        void videoRef.current?.play().catch(() => undefined);
      });
    } catch (cause) {
      const message = visibleFailure(cause, "Could not play");
      if (message) setError(message);
    }
  }

  async function download(recordingId: string) {
    setError(null);
    try {
      const source = await props.client.recordingContent(recordingId, "Download");
      const type = source.type.length === 0 ? "video/webm" : source.type;
      const transcript = type === "text/x-powershell-transcript" || type.startsWith("text/x-powershell-transcript;");
      const fallback = transcript ? "recording.txt" : "recording.webm";
      const blob = source.type === type ? source : new Blob([source], { type });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = recordingDownloadName(source) ?? fallback;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (cause) {
      const message = visibleFailure(cause, "Could not download");
      if (message) setError(message);
    }
  }

  return (
    <PortalChrome
      account={props.auth.account}
      apiBase={props.config.apiBase}
      environment={props.config.environment}
      access={access}
      adminHref={adminHref()}
      onSignOut={signOutOfPortal(props.auth)}
    >
      {signedIn ? null : <SignIn auth={props.auth} />}
      {signedIn ? (
        <SessionRecordings
          items={items}
          error={error}
          playbackRef={videoRef}
          playbackUrl={playbackUrl}
          playbackKind={playbackKind}
          playbackText={playbackText}
          onPlay={(recordingId) => void play(recordingId)}
          onDownload={(recordingId) => void download(recordingId)}
        />
      ) : null}
    </PortalChrome>
  );
}

function readBlob(source: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(reader.error ?? new Error("Transcript could not be read."));
    reader.readAsText(source);
  });
}

function signOutOfPortal(auth: OperatorAuth, before?: () => void): () => void {
  return () => {
    before?.();
    releaseQualityOnSignOut();
    void auth.signOut();
  };
}

function SignIn(props: { auth: OperatorAuth }) {
  return (
    <section className="panel sign-in">
      <h2>Sign in</h2>
      <p>Operators sign in with Entra ID. Anonymous access is not available.</p>
      <button type="button" onClick={() => void props.auth.signIn()}>
        Sign in
      </button>
    </section>
  );
}

function AdminDocument(props: {
  config: PortalConfig;
  auth: OperatorAuth;
  client: ControlClient;
  access?: MenuAccess | null;
  page: "overview" | "devices" | "users" | "roles" | "audit" | "recording";
}) {
  const access = useResolvedAccess(props.config.apiBase, props.auth, props.access);
  const [settingsPath, setSettingsPath] = useState(() => window.location.pathname);
  useEffect(() => {
    const next = canonicalSettingsPath(settingsPath);
    if (!next) return;
    window.history.replaceState(window.history.state, "", `${next}${window.location.search}`);
    setSettingsPath(next);
  }, [settingsPath]);
  if (props.page === "devices") {
    return <DeviceDocument config={props.config} auth={props.auth} client={props.client} access={access} surface="admin" />;
  }
  if (props.page === "audit") {
    return <AuditRoute config={props.config} auth={props.auth} client={props.client} access={access} />;
  }
  return (
    <PortalChrome
      account={props.auth.account}
      apiBase={props.config.apiBase}
      environment={props.config.environment}
      access={access}
      adminHref={adminHref()}
      onSignOut={signOutOfPortal(props.auth)}
    >
      {props.auth.account === null ? <SignIn auth={props.auth} /> : null}
      {props.auth.account !== null ? (
        <SettingsShell page={props.page} showDirectory={readsDirectory(access)} showRecording={readsRecording(access)}>
          {props.page === "overview" ? (
            <OverviewPage config={props.config} auth={props.auth} client={props.client} />
          ) : null}
          {props.page === "users" || props.page === "roles" ? (
            <DirectoryPage config={props.config} auth={props.auth} access={access} page={props.page} />
          ) : null}
          {props.page === "recording" ? <RecordingPage apiBase={props.config.apiBase} token={() => props.auth.token()} /> : null}
        </SettingsShell>
      ) : null}
    </PortalChrome>
  );
}

function OperatorDevices(props: {
  config: PortalConfig;
  auth: OperatorAuth;
  client: ControlClient;
  access?: MenuAccess | null;
}) {
  const access = useResolvedAccess(props.config.apiBase, props.auth, props.access);
  return <DeviceDocument config={props.config} auth={props.auth} client={props.client} access={access} surface="operator" />;
}

function DeviceDocument(props: {
  config: PortalConfig;
  auth: OperatorAuth;
  client: ControlClient;
  access: MenuAccess | null;
  surface: "operator" | "admin";
}) {
  const sessions = useMemo(() => createSessionWindows(), []);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [stealthError, setStealthError] = useState<string | null>(null);
  const signedIn = props.auth.account !== null;
  const allowConnect = props.access?.connect !== false;
  const canAdminister = props.access?.administer === true;
  const canDelete = canDeleteDevices(props.access) && portalDeviceDeleteEnabled(props.config.environment);
  const inventory = useDeviceInventory(props.client, signedIn);

  function start(deviceId: string, accessMode: SessionLaunch["accessMode"], features: SessionLaunch["features"], reason: string) {
    const row = inventory.rows.find((item) => item.deviceId === deviceId);
    console.info(consentClickLine(new Date()));
    const opened = sessions.open({
      deviceId,
      accessMode,
      features,
      displayName: row?.name ?? "",
      hostname: row?.hostname ?? "",
      reason,
    });
    setPopupBlocked(!opened.ok);
  }

  async function openStealth(deviceId: string, justification: string, reason = "", resumed = false) {
    setStealthError(null);
    try {
      const access = await props.auth.token();
      if (authTimeStale(bearerAuthTime(access), Math.floor(Date.now() / 1000))) {
        if (resumed) {
          setStealthError("Sign in again");
          return;
        }
        rememberStealth(deviceId, justification, reason);
        try {
          await demandStepUp();
        } catch {
          setStealthError("Sign in again");
        }
        return;
      }
      if (!resumed) rememberStealth(deviceId, justification, reason);
      const opened = await openStealthSession(
        props.config.apiBase,
        () => props.auth.token(),
        deviceId,
        justification,
        reason,
        !resumed,
      );
      sessionStorage.removeItem(PENDING_STEALTH);
      const child = window.open(
        `/stealth?sessionId=${opened.sessionId}`,
        `stealth-${opened.sessionId}`,
        "popup,width=1280,height=800",
      );
      setPopupBlocked(!child);
    } catch (cause) {
      if (isStepUpError(cause)) {
        setStealthError("Sign in again");
        return;
      }
      sessionStorage.removeItem(PENDING_STEALTH);
      const message = visibleFailure(cause, "Stealth Command could not open.");
      if (message) setStealthError(message);
    }
  }

  useEffect(() => {
    if (!signedIn) return;
    const pending = takeStealth();
    if (!pending) return;
    void openStealth(pending.deviceId, pending.justification, pending.reason, true);
  }, [signedIn]);

  const list = (
    <>
      {popupBlocked ? <p>{POPUP_BLOCKED}</p> : null}
      {stealthError ? <p role="alert">{stealthError}</p> : null}
      {props.surface === "admin" ? (
        <DevicePlacement
          devices={inventory.rows}
          catalog={inventory.catalog}
          error={inventory.error}
          canEdit={canAdminister}
          canDelete={canDelete}
          onRename={inventory.rename}
          onPlace={inventory.place}
          onCommit={inventory.commit}
          onApprove={inventory.approve}
          onDelete={inventory.remove}
        />
      ) : (
        <DeviceList
          devices={inventory.rows}
          catalog={inventory.catalog}
          error={inventory.error}
          load={inventory.load}
          now={() => correctedNow(Date.now(), serverClockOffsetMs())}
          allowConnect={allowConnect}
          canApprove={canAdminister}
          onStart={start}
          onStealth={(deviceId, justification, reason) => void openStealth(deviceId, justification, reason)}
        />
      )}
    </>
  );

  return (
    <PortalChrome
      account={props.auth.account}
      apiBase={props.config.apiBase}
      environment={props.config.environment}
      access={props.access}
      adminHref={adminHref()}
      onSignOut={signOutOfPortal(props.auth, () => sessions.closeAll())}
    >
      {signedIn ? null : <SignIn auth={props.auth} />}
      {signedIn ? (
        props.surface === "admin" ? (
          <SettingsShell page="devices" showDirectory={readsDirectory(props.access)} showRecording={readsRecording(props.access)}>{list}</SettingsShell>
        ) : (
          list
        )
      ) : null}
    </PortalChrome>
  );
}

function DirectoryPage(props: { config: PortalConfig; auth: OperatorAuth; access: MenuAccess | null; page: "users" | "roles" }) {
  const verify = new URLSearchParams(window.location.search).get("wyloVerify");
  const statusRows = import.meta.env.DEV && verify === "users-status";
  const fixture = import.meta.env.DEV && (verify === "admin" || verify === "roles" || statusRows);
  const [users, setUsers] = useState<DirectoryUser[]>(statusRows ? FIXTURE_STATUS_USERS : fixture ? FIXTURE_USERS : []);
  const [roles, setRoles] = useState<DirectoryRole[]>(fixture ? FIXTURE_ROLES : []);
  const [catalog, setCatalog] = useState<ScopeCatalog>(fixture ? FIXTURE_SCOPE_CATALOG : { organisations: [], departments: [] });
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    if (fixture || props.auth.account === null) return;
    const controller = new AbortController();
    void Promise.all([
      loadDirectoryUsers(props.config.apiBase, () => props.auth.token(), controller.signal),
      loadDirectoryRoles(props.config.apiBase, () => props.auth.token(), controller.signal),
      loadScopeCatalog(props.config.apiBase, () => props.auth.token(), controller.signal),
    ])
      .then(([nextUsers, nextRoles, nextCatalog]) => {
        if (controller.signal.aborted) return;
        if (nextUsers) setUsers(nextUsers);
        if (nextRoles) setRoles(nextRoles);
        setCatalog(nextCatalog);
        if (!nextUsers || !nextRoles) setNotice("Admin data is not available.");
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        const message = visibleFailure(cause, "Admin data is not available.");
        if (message) setNotice(message);
      });
    return () => controller.abort();
  }, [fixture, props.auth, props.config.apiBase]);

  async function onSetDisabled(userId: string, disabled: boolean) {
    if (fixture) {
      setUsers((current) => current.map((user) => (user.id === userId && !user.seeded ? { ...user, disabled } : user)));
      return;
    }
    try {
      const saved = await setDirectoryDisabled(props.config.apiBase, () => props.auth.token(), userId, disabled);
      if (!saved) {
        setNotice("That user could not be updated.");
        return;
      }
      setNotice(null);
      setUsers((current) => current.map((user) => (user.id === userId ? { ...user, disabled } : user)));
    } catch (cause) {
      const message = visibleFailure(cause, "That user could not be updated.");
      if (message) setNotice(message);
    }
  }

  const canManage = props.access?.manageDirectory === true;

  function replaceUser(saved: DirectoryUser) {
    setUsers((current) => current.map((user) => (user.id === saved.id ? saved : user)));
    setNotice(null);
  }

  async function onCreate(input: { userName: string; displayName: string; roles: string[] }) {
    if (fixture) {
      setUsers((current) => [
        ...current,
        {
          id: crypto.randomUUID(),
          userName: input.userName,
          displayName: input.displayName.length > 0 ? input.displayName : null,
          disabled: false,
          seeded: false,
          source: "manual",
          roles: input.roles,
        },
      ]);
      setNotice(null);
      return;
    }
    try {
      const saved = await createDirectoryUser(props.config.apiBase, () => props.auth.token(), input);
      if (!saved) {
        setNotice("That user could not be added.");
        return;
      }
      setUsers((current) => [...current, saved]);
      setNotice(null);
    } catch (cause) {
      const message = visibleFailure(cause, "That user could not be added.");
      if (message) setNotice(message);
    }
  }

  async function onSave(userId: string, input: { userName: string; displayName: string; roles: string[]; disabled: boolean }) {
    if (fixture) {
      setUsers((current) =>
        current.map((user) =>
          user.id === userId
            ? {
                ...user,
                userName: input.userName,
                displayName: input.displayName.length > 0 ? input.displayName : null,
                roles: input.roles,
                disabled: input.disabled,
              }
            : user,
        ),
      );
      setNotice(null);
      return;
    }
    try {
      const saved = await updateDirectoryUser(props.config.apiBase, () => props.auth.token(), userId, input);
      if (!saved) {
        setNotice("That user could not be updated.");
        return;
      }
      replaceUser(saved);
    } catch (cause) {
      const message = visibleFailure(cause, "That user could not be updated.");
      if (message) setNotice(message);
    }
  }

  async function onRemove(userId: string) {
    if (fixture) {
      setUsers((current) => current.filter((user) => user.id !== userId));
      setNotice(null);
      return;
    }
    try {
      const removed = await deleteDirectoryUser(props.config.apiBase, () => props.auth.token(), userId);
      if (!removed) {
        setNotice("That user could not be removed.");
        return;
      }
      setUsers((current) => current.filter((user) => user.id !== userId));
      setNotice(null);
    } catch (cause) {
      const message = visibleFailure(cause, "That user could not be removed.");
      if (message) setNotice(message);
    }
  }

  async function onCreateRole(input: { name: string; grants: RoleGrant[] }) {
    if (fixture) {
      setRoles((current) => [
        ...current,
        { id: crypto.randomUUID(), name: input.name, scope: scopeWord(input.grants), builtIn: false, grants: input.grants },
      ]);
      setNotice(null);
      return;
    }
    try {
      const saved = await createDirectoryRole(props.config.apiBase, () => props.auth.token(), input);
      if (!saved.role) {
        setNotice(saved.detail ?? "That role could not be added.");
        return;
      }
      setRoles((current) => [...current, saved.role!]);
      setNotice(null);
    } catch (cause) {
      const message = visibleFailure(cause, "That role could not be added.");
      if (message) setNotice(message);
    }
  }

  async function onSaveRole(roleId: string, input: { name: string; grants: RoleGrant[] }) {
    if (fixture) {
      setRoles((current) =>
        current.map((role) => (role.id === roleId && !role.builtIn ? { ...role, name: input.name, scope: scopeWord(input.grants), grants: input.grants } : role)),
      );
      setNotice(null);
      return;
    }
    try {
      const saved = await updateDirectoryRole(props.config.apiBase, () => props.auth.token(), roleId, input);
      if (!saved.role) {
        setNotice(saved.detail ?? "That role could not be updated.");
        return;
      }
      setRoles((current) => current.map((role) => (role.id === roleId ? saved.role! : role)));
      setNotice(null);
    } catch (cause) {
      const message = visibleFailure(cause, "That role could not be updated.");
      if (message) setNotice(message);
    }
  }

  async function onRemoveRole(roleId: string) {
    const role = roles.find((item) => item.id === roleId);
    if (role?.builtIn) {
      setNotice(role.name === "Global Admin" ? "The Global Admin role cannot be demoted." : "Built-in roles cannot be deleted.");
      return;
    }
    if (role && users.some((user) => user.roles.includes(role.name))) {
      setNotice("This role is still assigned to a user.");
      return;
    }
    if (fixture) {
      setRoles((current) => current.filter((item) => item.id !== roleId));
      setNotice(null);
      return;
    }
    try {
      const removed = await deleteDirectoryRole(props.config.apiBase, () => props.auth.token(), roleId);
      if (!removed.removed) {
        setNotice(removed.detail ?? "That role could not be deleted.");
        return;
      }
      setRoles((current) => current.filter((item) => item.id !== roleId));
      setNotice(null);
    } catch (cause) {
      const message = visibleFailure(cause, "That role could not be deleted.");
      if (message) setNotice(message);
    }
  }

  async function onSetUserRoles(userId: string, roles: string[]): Promise<string | null> {
    if (fixture) {
      setUsers((current) => current.map((user) => (user.id === userId ? { ...user, roles: [...roles] } : user)));
      return null;
    }
    const saved = await setDirectoryUserRoles(props.config.apiBase, () => props.auth.token(), userId, roles);
    if (!saved.user) return saved.detail ?? "That user could not be updated.";
    replaceUser(saved.user);
    return null;
  }

  if (props.page === "roles") {
    return (
      <RolesScreen
        roles={roles}
        users={users}
        notice={notice}
        canManage={canManage}
        catalog={catalog}
        onCreate={(input) => void onCreateRole(input)}
        onSave={(id, input) => void onSaveRole(id, input)}
        onRemove={(id) => void onRemoveRole(id)}
        onSetUserRoles={(userId, nextRoles) => onSetUserRoles(userId, nextRoles)}
      />
    );
  }
  return (
    <UsersScreen
      users={users}
      roles={roles}
      notice={notice}
      canManage={canManage}
      selfUserName={props.auth.account?.username ?? null}
      onSetDisabled={(id, disabled) => void onSetDisabled(id, disabled)}
      onCreate={(input) => void onCreate(input)}
      onSave={(id, input) => void onSave(id, input)}
      onRemove={(id) => void onRemove(id)}
    />
  );
}

function OverviewPage(props: { config: PortalConfig; auth: OperatorAuth; client: ControlClient }) {
  const [installerPending, setInstallerPending] = useState(false);
  const [installerError, setInstallerError] = useState<string | null>(null);
  const [macNote, setMacNote] = useState<string | null>(null);
  const [versions, setVersions] = useState<AgentVersions | null>(null);
  useEffect(() => {
    if (props.auth.account === null) return;
    let live = true;
    void props.client.agentVersions().then((next) => {
      if (live) setVersions(next);
    }).catch(() => {
      if (live) setVersions({ windows: { published: false, version: null }, macos: { published: false, version: null } });
    });
    return () => {
      live = false;
    };
  }, [props.auth.account, props.client]);

  async function downloadWindows() {
    setInstallerPending(true);
    setInstallerError(null);
    try {
      const file = await props.client.downloadAgentInstaller();
      const bytes = new Uint8Array(await file.bytes.arrayBuffer());
      saveDownload(file.fileName, bytes);
    } catch (cause) {
      const message = visibleFailure(cause, "Could not download the agent");
      if (message) setInstallerError(message);
    } finally {
      setInstallerPending(false);
    }
  }

  return (
    <SettingsOverview
      apiBase={props.config.apiBase}
      installerPending={installerPending}
      installerError={installerError}
      macNote={macNote}
      versions={versions}
      onDownloadWindows={() => void downloadWindows()}
      onDownloadMac={() => setMacNote("The macOS Apple silicon agent is not shipped yet.")}
    />
  );
}

function AuditRoute(props: {
  config: PortalConfig;
  auth: OperatorAuth;
  client: ControlClient;
  access: MenuAccess | null;
}) {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const trimmed = path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
    if (trimmed !== "/admin/audit" || !readsAudit(props.access)) return;
    const next = `/audit${window.location.search}`;
    window.history.replaceState(window.history.state, "", next);
    setPath("/audit");
  }, [path, props.access]);
  return (
    <PortalChrome
      account={props.auth.account}
      apiBase={props.config.apiBase}
      environment={props.config.environment}
      access={props.access}
      adminHref={adminHref()}
      onSignOut={signOutOfPortal(props.auth)}
    >
      {props.auth.account === null ? <SignIn auth={props.auth} /> : <AuditPage client={props.client} auth={props.auth} />}
    </PortalChrome>
  );
}

export function AuditPage(props: { client: ControlClient; auth: OperatorAuth }) {
  const [filters, setFilters] = useState<AuditFilters>(() => readAuditFilters(window.location.search));
  const [items, setItems] = useState<AuditLogRow[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const filterTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const signedIn = props.auth.account !== null;
  const inventory = useDeviceInventory(props.client, signedIn);
  const deviceNamesToken = useMemo(
    () => inventory.rows.map((device) => `${device.deviceId}:${device.name}`).join(";"),
    [inventory.rows],
  );
  const deviceNameFor = useCallback(
    (deviceId: string | null) => {
      if (!deviceId) return "None";
      const hit = inventory.rows.find((device) => device.deviceId === deviceId);
      return hit?.name ?? deviceId;
    },
    [inventory.rows],
  );
  const serverKey = serverAuditKey(filters, filterTimeZone);
  const [settledKey, setSettledKey] = useState(serverKey);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const timeZoneRef = useRef(filterTimeZone);
  timeZoneRef.current = filterTimeZone;
  useEffect(() => {
    const handle = window.setTimeout(() => setSettledKey(serverKey), AUDIT_REFETCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [serverKey]);
  useEffect(() => {
    if (props.auth.account === null) return;
    const controller = new AbortController();
    let live = true;
    void collectAuditEvents(props.client, filtersRef.current, controller.signal, timeZoneRef.current)
      .then((result) => {
        if (!live) return;
        setItems(result.items);
        setTruncated(result.truncated);
        setError(null);
      })
      .catch((cause: unknown) => {
        const message = visibleFailure(cause, "Could not load the audit log");
        if (!live || controller.signal.aborted || !message) return;
        setError(message);
      });
    return () => {
      live = false;
      controller.abort();
    };
  }, [props.auth.account, props.client, settledKey]);
  return (
    <AuditLog
      items={items}
      error={error}
      truncated={truncated}
      filters={filters}
      filterTimeZone={filterTimeZone}
      deviceNameFor={deviceNameFor}
      deviceNamesToken={deviceNamesToken}
      onFiltersChange={(next) => {
        writeAuditFilters(next);
        setFilters(next);
      }}
    />
  );
}

function SessionDocument(props: {
  config: PortalConfig;
  auth: OperatorAuth;
  client: ControlClient;
}) {
  const boot = bootSessionDocument({
    host: window,
    api: props.client,
    apiBase: props.config.apiBase,
    openMedia: fixtureMedia(window.location.search),
    saveFile: (name, bytes) => {
      if (isPng(bytes)) {
        holdLocalClipboard({ png: bytes });
        return;
      }
      void placeLocalClipboard({ file: { name, bytes } }).then((placed) => {
        if (placed === "refused") saveDownload(name, bytes);
      });
    },
    who: () => operatorWho(props.auth.account),
  });
  return (
    <div className="session-frame">
      {boot.role === "unreadable-session" ? <p>{boot.message}</p> : null}
      {boot.role === "already-started" ? <p>{ALREADY_STARTED}</p> : null}
      {boot.role === "session" ? (
        <LiveSession
          desk={boot.desk}
          client={props.client}
          environment={props.config.environment}
          deviceId={boot.deviceId}
          deviceName={boot.deviceName}
        />
      ) : null}
    </div>
  );
}

function LiveSession(props: {
  desk: Desk;
  client: ControlClient;
  environment: PortalConfig["environment"];
  deviceId: string;
  deviceName: string;
}) {
  const quality = useSessionQuality(props.client, props.desk);
  const screen = useSyncExternalStore(props.desk.subscribe, props.desk.screen, props.desk.screen);
  const viewer = useViewerReadings(props.desk, screen.kind === "live");
  const shown = applyViewerReadings(quality.quality, viewer);
  const accessMode = useSyncExternalStore(props.desk.subscribe, props.desk.accessMode, props.desk.accessMode);
  const cadGate = useSyncExternalStore(props.desk.subscribe, props.desk.cadGate, props.desk.cadGate);
  const cadNotice = useSyncExternalStore(props.desk.subscribe, props.desk.cadNotice, props.desk.cadNotice);
  const linkNotice = useSyncExternalStore(props.desk.subscribe, props.desk.linkNotice, props.desk.linkNotice);
  const clipboardNotice = useSyncExternalStore(props.desk.subscribe, props.desk.clipboardNotice, props.desk.clipboardNotice);
  return (
    <SessionScreen
      screen={screen}
      accessMode={accessMode}
      videoRef={(node) => props.desk.attach(node)}
      onSelectDisplay={(displayId) => void props.desk.selectDisplay(displayId)}
      onSetView={(view) => props.desk.setView(view)}
      onEnd={() => void props.desk.end()}
      onRemotePaste={(payload) => void props.desk.pasteToDevice(payload)}
      onGesture={() => props.desk.flushClipboard()}
      onPointer={(pointer) => props.desk.pointer(pointer)}
      onKey={(key) => props.desk.key(key)}
      onWheel={(wheel) => props.desk.wheel(wheel)}
      cadGate={cadGate}
      cadNotice={cadNotice}
      linkNotice={linkNotice}
      clipboardNotice={clipboardNotice}
      quality={shown}
      qualityArrivals={quality.arrivals}
      environment={props.environment}
      deviceId={props.deviceId}
      deviceName={props.deviceName}
      onSecureAttention={() => void props.desk.secureAttention()}
    />
  );
}

export function useViewerReadings(desk: Desk, live: boolean): ViewerReading[] {
  const [readings, setReadings] = useState<ViewerReading[]>([]);
  useEffect(() => {
    if (!live) {
      setReadings([]);
      return;
    }
    return watchViewerLink(() => desk.readLink(), (reading) => {
      setReadings((current) => [...current, reading].slice(-60));
    });
  }, [desk, live]);
  return live ? readings : [];
}

export function useSessionQuality(client: ControlClient, desk: Desk): { quality: SessionQualityView | null; arrivals: number[] } {
  const [quality, setQuality] = useState<SessionQualityView | null>(null);
  const [arrivals, setArrivals] = useState<number[]>([]);
  const log = useRef<ArrivalLog>(emptyArrivalLog());
  useEffect(() => {
    if (!client.getQuality) return;
    if (new URLSearchParams(window.location.search).get("wyloVerify")) return;
    let live = true;
    const tick = () => {
      const sessionId = desk.sessionId();
      if (!sessionId) return;
      void client.getQuality?.(sessionId).then((row) => {
        if (!live || !row) return;
        setQuality(row);
        const noted = noteQualityArrival(log.current, row.samples, Date.now());
        if (noted === log.current) return;
        log.current = noted;
        setArrivals(noted.arrivals);
      }).catch(() => undefined);
    };
    tick();
    const timer = setInterval(tick, QUALITY_POLL_MS);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [client, desk]);
  return { quality, arrivals };
}

function saveDownload(name: string, bytes: Uint8Array) {
  const copy = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(copy).set(bytes);
  const blob = new Blob([copy]);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
}
